<template>
  <info-panel
    v-if="resource"
    :item="resource"
    :title="resource?.title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>
      {{ $t("labels.showEmptySections") }}
    </template>
    <template #label-valueInHeader>
      {{ $t("labels.valueInHeader") }}
    </template>

    <template #header v-if="settingsValues.valueInHeader && sceneNode && (sceneNode.data.valueVisible || sceneNode.data.barVisible)">
      <div>
        <h2 class="info-panel__type">{{ $t("words.currentValue") }}</h2>
        <net-item-value-bar
          v-if="sceneNode.data.barVisible && minValue != null && maxValue != null"
          :value="calculatedValue?.value ?? resource.value"
          :unit="calculatedValue?.unit ?? ''"
          :min-value="minValue"
          :max-value="maxValue"
          :value-visible="sceneNode.data.valueVisible"
          class="target-infopanel__value"
        />
        <net-item-value-plain
          v-else-if="sceneNode.data.valueVisible"
          :value="calculatedValue?.value"
          :unit="calculatedValue?.unit ?? ''"
          class="target-infopanel__value"
        />
      </div>
    </template>

    <template #label-image>
      {{ $t("words.image") }}
    </template>
    <template
      #image
      v-if="
        imageData &&
        (extraSettings.image?.value == 'side-full' ||
          extraSettings.image?.value == 'side-cut')
      "
    >
      <img
        :src="imageData"
        :class="{
          'info-panel__image-full': extraSettings.image?.value == 'side-full',
          'info-panel__image-cut': extraSettings.image?.value == 'side-cut'
        }"
      />
    </template>
    <template
      #header-image
      v-if="imageData && extraSettings.image?.value == 'icon'"
    >
      <img :src="imageData" />
    </template>
    <template #option-image-icon>{{ $t("labels.imageAsIcon") }}</template>
    <template #option-image-hidden>{{ $t("labels.hidden") }}</template>
    <template #option-image-side-full>{{
      $t("labels.imageAtSideFull")
    }}</template>
    <template #option-image-side-cut>{{
      $t("labels.imageAtSideCut")
    }}</template>

    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template
      #description
      v-if="settingsValues.showEmptySections || !isEmpty(resource.description)"
    >
      <formatted-read-more-text
        :value="resource.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <template #label-value>
      {{ $t("labels.value") }}
    </template>
    <template #value>
      <div class="info-panel__table">
        <span class="label">{{ $t("words.currentValue") }}:</span>
        <span>
          {{ formatNumber(calculatedValue?.value) }}
          {{ calculatedValue?.unit }}
        </span>

        <span class="label">{{ $t("words.startValue") }}:</span>
        <span>
          {{ formatNumber(startValue?.value) }}
          {{ startValue?.unit }}
        </span>

        <span class="label">{{ $t("labels.valueRange") }}:</span>
        <span>
          {{ formatNumber(minValue) }} –
          {{ formatNumber(maxValue) }}
        </span>
      </div>
    </template>

    <template #label-timechart>
      {{ $t("labels.stateOverTime") }}
    </template>
    <template #timechart="{ width }">
      <Chart
        v-if="isSimulationOpen()"
        :width="width"
        :height="300"
        :chart-item="chartOptions"
      />
      <p v-else class="message mv-0 message-info">
        {{ $t("messages.runSimulationForChart") }}
      </p>
    </template>

    <template #label-impacts>
      {{ $t("objectNames.impact", 5) }}
    </template>
    <template
      #impacts
      v-if="settingsValues.showEmptySections || relations.length > 0"
    >
      <div class="line--half gap-4">
        <div>
          <span class="label" v-if="incomingRelations.length > 0">
            {{ $t("labels.incomingRelations") }}
          </span>
          <button-list
            :items="incomingRelations"
            :transition-active="false"
            @select="openOverlay($event)"
          >
            <template #default="{ item: relation }: { item: RelationItem }">
              <span v-if="items[relation.startItemType][relation.startItemId]?.title">
                {{ items[relation.startItemType][relation.startItemId].title }}
              </span>
              <span v-else class="placeholder-text">
                {{ $t("labels.unnamed") }}
              </span>
            </template>
            <template #placeholder>
              <p class="message mv-0">
                {{ $t("errors.noIncomingRelations") }}
              </p>
            </template>
          </button-list>
        </div>
        <div>
          <span class="label" v-if="outgoingRelations.length > 0">{{
            $t("labels.outgoingRelations")
          }}</span>
          <button-list
            :items="outgoingRelations"
            :transition-active="false"
            @select="openOverlay($event)"
          >
            <template #default="{ item: relation }: { item: RelationItem }">
              <span v-if="items[relation.endItemType][relation.endItemId]?.title">
                {{ items[relation.endItemType][relation.endItemId].title }}
              </span>
              <span v-else class="placeholder-text">
                {{ $t("labels.unnamed") }}
              </span>
            </template>
            <template #placeholder>
              <p class="message mv-0">
                {{ $t("errors.noOutgoingRelations") }}
              </p>
            </template>
          </button-list>
        </div>
      </div>
    </template>

    <template #label-flows>
      {{ $t("objectNames.flow", 5) }}
    </template>
    <template
      #flows
      v-if="settingsValues.showEmptySections || flowBranches.length > 0"
    >
      <div class="line--half gap-4">
        <div>
          <span class="label" v-if="incomingFlows.length > 0">
            {{ $t("labels.incomingFlows") }}
          </span>
          <button-list
            :items="incomingFlows"
            :transition-active="false"
            @select="openOverlay({ type: 'flow', id: $event.flowId })"
          >
            <template #default="{ item: branch }: { item: FlowBranchItem }">
              <span>{{ flowStore.titles[branch.flowId] }}</span>
            </template>
            <template #placeholder>
              <p class="message mv-0">
                {{ $t("errors.noIncomingFlows") }}
              </p>
            </template>
          </button-list>
        </div>
        <div>
          <span class="label" v-if="outgoingFlows.length > 0">
            {{ $t("labels.outgoingFlows") }}
          </span>
          <button-list
            :items="outgoingFlows"
            :transition-active="false"
            @select="openOverlay({ type: 'flow', id: $event.flowId })"
          >
            <template #default="{ item: branch }: { item: FlowBranchItem }">
              <span>{{ flowStore.titles[branch.flowId] }}</span>
            </template>
            <template #placeholder>
              <p class="message mv-0">
                {{ $t("errors.noOutgoingFlows") }}
              </p>
            </template>
          </button-list>
        </div>
      </div>
    </template>

    <template #label-actions>
      {{ $t("objectNames.action", 5) }}
    </template>
    <template #heading-actions>{{
      $t("labels.influencingActions")
    }}</template>
    <template
      #actions
      v-if="settingsValues.showEmptySections || actions.length > 0"
    >
      <div v-if="actions.length > 0" class="line_effect">
        <template v-for="action in actions" :key="action.id">
          <button-item @click="openOverlay(action)">
            <span v-if="action.title">
              {{ action.title }}
            </span>
            <span v-else class="placeholder-text">
              {{ $t("labels.unnamed") }}
            </span>
          </button-item>
          <effect-amount
            v-if="actionEffects[action.id]"
            :effect="actionEffects[action.id]!"
            :inline-expression="true"
          />
        </template>
      </div>
      <p v-else class="message mv-0">
        {{ $t("errors.noInfluencingActions") }}
      </p>
    </template>

    <template #label-incidents>
      {{ $t("objectNames.incident", 5) }}
    </template>
    <template #heading-incidents>{{
      $t("labels.influencingIncidents")
    }}</template>
    <template
      #incidents
      v-if="settingsValues.showEmptySections || incidents.length > 0"
    >
      <div v-if="incidents.length > 0" class="line_effect">
        <template v-for="incident in incidents" :key="incident.id">
          <button-item @click="openOverlay(incident)">
            <span v-if="incident.title">
              {{ incident.title }}
            </span>
            <span v-else class="placeholder-text">
              {{ $t("labels.unnamed") }}
            </span>
          </button-item>
          <effect-amount
            :effect="incidentEffects[incident.id]"
            :inline-expression="true"
          />
        </template>
      </div>
      <p v-else class="message mv-0">
        {{ $t("errors.noInfluencingIncidents") }}
      </p>
    </template>

    <template #label-scenarios>
      {{ $t("objectNames.scenario", 5) }}
    </template>
    <template
      #scenarios
      v-if="settingsValues.showEmptySections || changingScenarios.length > 0"
    >
      <button-list
        :items="changingScenarios"
        line-class="line_effect"
        @select="openOverlay($event)"
      >
        <template #default="{ item }">
          <span v-if="item.title">{{ item.title }}</span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </template>
        <template #after="{ item }">
          <span class="ta-r">{{ item.resourceValues[resource.id] }}</span>
        </template>
        <template #placeholder>
          <p class="message mv-0">
            {{ $t("errors.nostartValuesInScenarios") }}
          </p>
        </template>
      </button-list>
    </template>

    <template #label-stakeholders>
      {{ $t("objectNames.stakeholder", 2) }}
    </template>
    <template
      #stakeholders
      v-if="settingsValues.showEmptySections || stakeholders.length > 0"
    >
      <button-list
        :items="stakeholders"
        prop="title"
        :transition-active="false"
        @select="openOverlay($event)"
      >
        <template #default="{ item: stakeholder }">
          <span v-if="stakeholder.title">{{ stakeholder.title }}</span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </template>
        <template #placeholder>
          <p class="message mv-0">
            {{ $t("errors.noStakeholdersAssigned") }}
          </p>
        </template>
      </button-list>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useRoute } from "vue-router";
import { omitBy, mapValues, find, isNil, map } from "lodash-es";
import { ensureInt, notNull } from "@/helpers/utility/Type";
import { pickBy } from "@/helpers/utility/CollectionFunctions";
import { formatNumber } from "@/helpers/utility/Formatting";
import { isEmpty } from "@/helpers/utility/Text";

import ButtonList from "@core/ui/ButtonList.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";

import EffectAmount from "@editor/items/effect/EffectAmount.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import NetItemValueBar from "@editor/net/NetItemValueBar.vue";
import NetItemValuePlain from "@editor/net/NetItemValuePlain.vue";
import Chart from "@editor/items/charts/Chart.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useStakeholderStore } from "@editor/items/stakeholder/stakeholders.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useResourceStore } from "./resources.store";
import { useIndexStore } from "../index/indices.store";
import { useImageStore } from "../image/images.store";
import { useFlowStore } from "../flow/flows.store";
import { useFlowBranchStore } from "../flow/flowBranches.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useSceneTree } from "../scene/sceneTree";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { RelationItem } from "../relation/RelationItem";
import type { ResourceItem } from "./ResourceItem";
import type { EffectItem } from "../effect/EffectItem";
import type { FlowBranchItem } from "../flow/FlowBranchItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { ChartItemOptions } from "../charts/useChartData";

const props = defineProps<{
  resource: ResourceItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const route = useRoute();
const imageStore = useImageStore();
const flowStore = useFlowStore();
const simulationViewStore = useSimulationViewStore();

const sections = reactive({
  description: true,
  timechart: true,
  value: true,
  impacts: true,
  flows: true,
  actions: true,
  incidents: true,
  scenarios: true,
  stakeholders: true
});

const extraSettings = reactive({
  image: {
    type: "dropdown" as const,
    value: "icon",
    options: ["icon", "side-full", "side-cut", "hidden"]
  },
  showEmptySections: { type: "check" as const, value: true },
  valueInHeader: { type: "check" as const, value: true }
});

const infoPanelStore = useInfoPanelStore();

const targets = computed(() => {
  return useTargetStore().items;
});
const resources = computed(() => {
  return useResourceStore().items;
});
const indices = computed(() => {
  return useIndexStore().items;
});
const items = computed(() => {
  return {
    target: targets.value,
    resource: resources.value,
    index: indices.value
  };
});

// VALUE

const startValue = computed(() => {
  if (props.resource == null) return null;
  return useResourceStore().expressionValues[props.resource.uid] ?? null;
});

const calculatedValue = computed(() => {
  if (props.resource == null) return null;
  if (!isSimulationOpen()) return startValue.value;
  return simulationViewStore.elementValues[props.resource.uid];
});

const minValue = computed(() => {
  if (props.resource == null) return null;
  return props.resource.minValue;
});
const maxValue = computed(() => {
  if (props.resource == null) return null;
  return props.resource.maxValue;
});

function isSimulationOpen() {
  if (!["simulation", "inspector"].includes(route.meta.areaName as string))
    return false;
  if (route.meta.areaName === "inspector") {
    if (useInspectorView().subview != "matrix") return false;
  }
  return simulationViewStore.simulation != null;
}

// ================
// NODE

const sceneTree = useSceneTree();

const treeNode = computed(() => {
  if (props.resource == null) return null;
  return sceneTree.elementNodes.get(props.resource.uid) || null;
});
const sceneNode = computed(() => treeNode.value?.node || null);

// ================
// CHART

const maxRoundRange = computed(() => {
  return Math.max(useSettingsStore().simulationRoundAmount - 1, (simulationViewStore.simulation?.maxRounds ?? 0) - 1, 0);
});

const chartOptions = computed<ChartItemOptions>(() => ({
  uid: props.resource?.uid + "-infochart",
  chartType: "line",
  data: {
    resource: [props.resource?.id ?? -1],
    simulation: [-1],
    simulationRound: [0, maxRoundRange.value]
  },
  chartSettings: {
    scaleColorsVisible: false,
    roundEffectsVisible: false,
    titleVisible: false,
    groupLabelsVisible: false
  },
  itemConfigs: {
    resource: {
      [props.resource?.id ?? -1]: {
        color: "var(--color-text)",
        open: false
      }
    }
  }
}));

// Relations

const incomingRelations = computed((): RelationItem[] => {
  if (props.resource == null) return [];
  return useRelationStore().byEndUid[props.resource.uid] ?? [];
});
const outgoingRelations = computed((): RelationItem[] => {
  if (props.resource == null) return [];
  return useRelationStore().byStartUid[props.resource.uid] ?? [];
});
const relations = computed(() => {
  return [...incomingRelations.value, ...outgoingRelations.value];
});

// Flows

const flowBranches = computed(() => {
  if (props.resource == null) return [];
  return useFlowBranchStore().byElementUid[props.resource.uid] ?? [];
});
const incomingFlows = computed(() => {
  return flowBranches.value.filter(
    (branch) => branch.branchType == "sink"
  );
});
const outgoingFlows = computed(() => {
  return flowBranches.value.filter(
    (branch) => branch.branchType == "source"
  );
});

const actions = computed(() => {
  if (props.resource == null) return [];
  const actions = useActionStore().byResource[props.resource.id];
  if (actions == null) return [];
  return Object.values(actions);
});
const actionEffects = computed(() => {
  const effectStore = useEffectStore();
  return omitBy(
    mapValues(effectStore.byAction, (effects) =>
      find(
        effects,
        (effect) =>
          effect.affectedType === "resource" &&
          effect.affectedId === props.resource?.id
      )
    ),
    isNil
  );
});

const incidentEffects = computed(() => {
  const effectStore = useEffectStore();
  return pickBy(
    mapValues(effectStore.byIncident, (effects) =>
      find(
        effects,
        (effect) =>
          effect.affectedType === "resource" &&
          effect.affectedId === props.resource?.id
      )
    ),
    notNull
  ) as { [key: number]: EffectItem };
});

const incidents = computed(() => {
  const incidentStore = useIncidentStore();
  const incidentIds = map(incidentEffects.value, (effect) => {
    if (effect.activatorId == null) return null;
    return ensureInt(effect.activatorId);
  }).filter(notNull);
  return incidentStore.getArrayByIds(incidentIds);
});

const changingScenarios = computed(() => {
  const scenarioStore = useScenarioStore();
  return scenarioStore.orderedItems.filter((scenario) =>
    Object.keys(scenario.resourceValues ?? {}).includes(props.resource?.id + "")
  );
});
const stakeholders = computed(() => {
  if (props.resource == null) return [];
  return useStakeholderStore().byResource[props.resource.id] ?? [];
});

const image = computed(() => {
  if (sceneNode.value && sceneNode.value.data.imageId != null) {
    return imageStore.items[sceneNode.value.data.imageId];
  }
  return null;
});

const imageData = computed(() => {
  if (!image.value) return undefined;
  return imageStore.mediaData[image.value.id] ?? undefined;
});

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}

function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.resource = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.resource || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as keyof typeof sections] = savedSections[key];
    }
  }
}

function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.resource = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.resource || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as keyof typeof extraSettings] = {
        ...(savedExtraSettings[key] as any)
      };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
loadSections();
</script>

<style></style>
